import React from "react";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { useDispatch, useSelector } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";
import { getDefaultSubscription } from "src/ui/utils/usePrefetch";

import { updateGroupMembers } from "@dashboard/devices/store/index";
import { useAllWorkspaces, useCurrentWorkspace, useSubscriptions } from "@dashboard/workspaces/api/helpers";
import { ConfirmDialog } from "@devices/components/shared";
import { useGroupDeleteGate } from "@groups/hooks/useGroupDeleteGate";
import { selectGroupById } from "@groups/store/selectors";
import { DialogContentText, useTheme } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { CellContext } from "@tanstack/react-table";

import { GroupOverviewData } from "../../GroupPage.utils";
import DeleteGroupDialog from "../DeleteGroupDialog";
import EditGroupDialog from "../EditGroupDialog";

export const ActionCell = (props: CellContext<GroupOverviewData, unknown>) => {
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [leaveDialogOpen, setLeaveDialogOpen] = React.useState(false);

    const { t } = useTranslation(["pages", "modals", "common"]);
    const theme = useTheme();
    const dispatch = useDispatch();

    const { data: workspaces = [] } = useAllWorkspaces();
    const { data: workspace } = useCurrentWorkspace();
    const { data: subscriptions = [] } = useSubscriptions();
    const group = useSelector((state) => selectGroupById(state, props.row.original.id));

    const { isDeleteDisabled, blockedReason } = useGroupDeleteGate(props.row.original.id);

    const currentUser = AuthService.getCurrentUser();
    const isGroupMember = group?.members.some((item) => item.email === currentUser?.email);

    const switchToDefaultWorkspace = async () => {
        const currentWorkspaceId = workspace?.id;
        const stillHasAccess = workspaces?.some((ws) => ws.id === currentWorkspaceId);

        if (!stillHasAccess && workspaces?.length > 0) {
            const defaultSubscription = getDefaultSubscription(subscriptions);
            const defaultWorkspace = workspaces.find((ws) => ws.subscription.id === defaultSubscription?.id);

            if (defaultWorkspace) {
                dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
                showSuccessToast(t("pages:groupPage.toast.switchedWorkspaceSuccess", { name: defaultWorkspace.name }));
            } else if (workspaces?.length > 0) {
                dispatch(setCurrentWorkspaceId(workspaces[0].id));
                showSuccessToast(t("pages:groupPage.toast.switchedWorkspaceSuccess", { name: workspaces[0].name }));
            }
        }
    };

    const handleLeaveGroup = async () => {
        if (!currentUser) {
            return;
        }

        try {
            await dispatch(updateGroupMembers({ id: props.row.original.id, removedMembers: [currentUser.email] })).unwrap();
            showSuccessToast(t("pages:groupPage.toast.leftGroupSuccess"));
            await switchToDefaultWorkspace();
        } catch (error) {
            const err = error as { message: string };
            showErrorToast(err.message);
        }
    };

    const deleteTooltip = blockedReason || t("pages:groupPage.tooltip.deleteGroup");

    return (
        <>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: "flex",
                    gap: theme.spacing(1),
                    justifyContent: "flex-end",
                }}
            >
                <SecondaryButton
                    tooltipProps={{ title: t("pages:groupPage.button.editGroup") }}
                    onClick={() => setEditDialogOpen(true)}
                    name="edit-group"
                >
                    <i className="fa-solid fa-fw fa-pencil" />
                </SecondaryButton>
                <DangerOutlinedButton
                    name="leave-group"
                    onClick={() => setLeaveDialogOpen(true)}
                    disabled={!isGroupMember}
                    tooltipProps={{
                        title: !isGroupMember
                            ? t("pages:groupPage.tooltip.notDirectMemberOfGroup")
                            : t("pages:groupPage.tooltip.leaveGroup"),
                    }}
                >
                    <i className="fa-solid fa-right-from-bracket" />
                </DangerOutlinedButton>
                <DangerOutlinedButton
                    name="delete-group"
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={isDeleteDisabled}
                    tooltipProps={{ title: deleteTooltip }}
                >
                    <i className="fa-solid fa-trash" />
                </DangerOutlinedButton>
            </div>

            <EditGroupDialog group={props.row.original} open={editDialogOpen} onClose={() => setEditDialogOpen(false)} />
            {group ? (
                <DeleteGroupDialog group={group} open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} />
            ) : null}
            <ConfirmDialog
                dangerButton
                title={t("modals:leaveGroup.title")}
                open={leaveDialogOpen}
                onConfirm={handleLeaveGroup}
                onClose={() => setLeaveDialogOpen(false)}
            >
                <DialogContentText>{t("modals:leaveGroup.description")}</DialogContentText>
            </ConfirmDialog>
        </>
    );
};
